import type { OperatorStatus, TransferTarget } from './types';

const OPERATOR_STATUSES = new Set<OperatorStatus>([
  'AVAILABLE',
  'BUSY',
  'CALL',
  'POST_PROCESSING',
  'BREAK',
  'LUNCH',
  'DND',
  'OFFLINE',
]);

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

export function normalizeTransferTargets(value: unknown): TransferTarget[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap<TransferTarget>(item => {
    const source = recordValue(item);
    if (!source) return [];

    if (source.type === 'GROUP') {
      const groupId = String(source.groupId || '').trim().toLowerCase();
      const destination = String(source.destination || `group:${groupId}`).trim();
      try { normalizeTransferDestination(destination); }
      catch { return []; }
      if (!groupId || !String(source.name || '').trim()) return [];
      return [{
        type: 'GROUP' as const,
        targetId: String(source.targetId || `group:${groupId}`),
        destination,
        groupId,
        name: String(source.name),
        strategy: source.strategy === 'SEQUENTIAL' ? 'SEQUENTIAL' as const : 'RING_ALL' as const,
        recipientCount: Math.max(0, Math.trunc(Number(source.recipientCount) || 0)),
        chain: Array.isArray(source.chain) ? source.chain.map(String).filter(Boolean) : [],
      }];
    }

    if (source.type !== undefined && source.type !== 'USER') return [];
    const userId = String(source.userId || '').trim();
    const extension = String(source.extension || '').trim();
    if (!userId || !/^\d{2,8}$/.test(extension)) return [];
    const rawStatus = String(source.operatorStatus || 'OFFLINE') as OperatorStatus;
    return [{
      type: 'USER' as const,
      targetId: String(source.targetId || `user:${userId}`),
      destination: String(source.destination || extension),
      userId,
      fullName: String(source.fullName || ''),
      extension,
      operatorStatus: OPERATOR_STATUSES.has(rawStatus) ? rawStatus : 'OFFLINE',
    }];
  });
}

export function filterTransferTargets(
  targets: TransferTarget[],
  search: string,
): TransferTarget[] {
  const query = String(search || '').trim().toLocaleLowerCase('ru-RU');
  if (!query) return targets;
  return targets.filter(target => {
    const searchable = target.type === 'GROUP'
      ? [target.name, ...(target.chain || [])]
      : [target.fullName, target.extension];
    return searchable
      .join(' ')
      .toLocaleLowerCase('ru-RU')
      .includes(query);
  });
}

function recipientLabel(count: number): string {
  const normalized = Math.max(0, Math.trunc(Number(count) || 0));
  const modulo100 = normalized % 100;
  const modulo10 = normalized % 10;
  const noun = modulo100 >= 11 && modulo100 <= 14
    ? 'сотрудников'
    : modulo10 === 1
      ? 'сотрудник'
      : modulo10 >= 2 && modulo10 <= 4
        ? 'сотрудника'
        : 'сотрудников';
  return `${normalized} ${noun}`;
}

export function transferTargetTitle(target: TransferTarget): string {
  if (target.type === 'GROUP') return target.name || 'Группа маршрутизации';
  return target.fullName || `Добавочный ${target.extension}`;
}

export function transferTargetSubtitle(target: TransferTarget): string {
  if (target.type === 'GROUP') {
    const strategy = target.strategy === 'SEQUENTIAL' ? 'Последовательно' : 'Одновременно';
    return `${strategy} · ${recipientLabel(target.recipientCount)}`;
  }
  return `доб. ${target.extension}`;
}

export function transferTargetInitials(target: TransferTarget): string {
  if (target.type === 'GROUP') return 'ГР';
  return target.fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('ru-RU') || '—';
}

export function normalizeTransferDestination(destination: string): string {
  const normalized = String(destination || '').trim();
  if (/^\d{2,8}$/.test(normalized)) return normalized;
  if (/^group:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)) {
    return normalized;
  }
  throw new Error('Выберите сотрудника или группу');
}

export function transferProgressMessage(): string {
  return 'Перевод звонка выполняется…';
}

export function transferTargetStatusLabel(status: OperatorStatus): string {
  const labels: Record<OperatorStatus, string> = {
    AVAILABLE: 'Доступен',
    BUSY: 'Занят',
    CALL: 'Разговор',
    POST_PROCESSING: 'Постобработка',
    BREAK: 'Перерыв',
    LUNCH: 'Обед',
    DND: 'Не беспокоить',
    OFFLINE: 'Офлайн',
  };
  return labels[status] || 'Офлайн';
}
